import { useCallback, useEffect, useMemo, useState } from 'react'

const MAX_RECENT_SEARCHES = 5
const STORAGE_VERSION = 'v1'

const storageKeyFor = (userKey, scope) =>
  `highlights.searches.${STORAGE_VERSION}.${encodeURIComponent(userKey)}.${scope}`

const cloneFilters = filters => JSON.parse(JSON.stringify(filters || {}))

const normalizeForSignature = value => {
  if (Array.isArray(value)) {
    return value.map(normalizeForSignature).sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b))
    )
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = normalizeForSignature(value[key])
        return result
      }, {})
  }

  return value
}

const filterSignature = filters =>
  JSON.stringify(normalizeForSignature(filters || {}))

const emptyState = () => ({
  last: null,
  recent: []
})

const readState = storageKey => {
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return emptyState()

    const parsed = JSON.parse(raw)
    return {
      last: parsed && parsed.last && typeof parsed.last === 'object'
        ? parsed.last
        : null,
      recent: Array.isArray(parsed?.recent)
        ? parsed.recent.filter(entry => entry && entry.filters).slice(0, MAX_RECENT_SEARCHES)
        : []
    }
  } catch (error) {
    console.warn('Unable to read saved searches', error)
    return emptyState()
  }
}

const writeState = (storageKey, next) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(next))
  } catch (error) {
    console.warn('Unable to save searches', error)
  }
}

export const hasSavedFilterValues = filters =>
  Object.values(filters || {}).some(value => {
    if (Array.isArray(value)) return value.length > 0
    return Boolean(value)
  })

export default function useRecentSearches ({ userKey, scope }) {
  const storageKey = useMemo(
    () => storageKeyFor(userKey || 'local-e2e', scope),
    [scope, userKey]
  )
  const [state, setState] = useState(() => readState(storageKey))

  useEffect(() => {
    setState(readState(storageKey))
  }, [storageKey])

  const updateState = useCallback(updater => {
    setState(current => {
      const next = updater(current)
      writeState(storageKey, next)
      return next
    })
  }, [storageKey])

  const rememberSearch = useCallback((
    filters,
    label,
    { addToRecent = true } = {}
  ) => {
    const savedFilters = cloneFilters(filters)
    const signature = filterSignature(savedFilters)

    updateState(current => {
      const recent = addToRecent && hasSavedFilterValues(savedFilters)
        ? [
            {
              filters: savedFilters,
              label: label || 'Saved search',
              savedAt: new Date().toISOString(),
              signature
            },
            ...current.recent.filter(entry =>
              (entry.signature || filterSignature(entry.filters)) !== signature
            )
          ].slice(0, MAX_RECENT_SEARCHES)
        : current.recent

      return {
        last: savedFilters,
        recent
      }
    })
  }, [updateState])

  const clearLastSearch = useCallback(() => {
    updateState(current => ({
      ...current,
      last: null
    }))
  }, [updateState])

  const clearRecentSearches = useCallback(() => {
    updateState(current => ({
      ...current,
      recent: []
    }))
  }, [updateState])

  return {
    lastSearch: state.last,
    recentSearches: state.recent,
    rememberSearch,
    clearLastSearch,
    clearRecentSearches
  }
}
